import { useMemo } from 'react';
import { SESSIONS, type MarketEvent } from '@/mocks/marketCalendar';
import { toUtcMinutes } from '../calculations';
import { StatCard } from './ui';

function bucketOf(utc: number): number {
  return Math.floor(utc / 120);
}
function bucketLabel(b: number): string {
  return `${String(b * 2).padStart(2, '0')}–${String(b * 2 + 2).padStart(2, '0')}`;
}

function sessionOf(utc: number): string {
  for (const s of SESSIONS) {
    const open =
      s.startUtc < s.endUtc
        ? utc >= s.startUtc && utc < s.endUtc
        : utc >= s.startUtc || utc < s.endUtc;
    if (open) return s.name;
  }
  return 'Sydney';
}

export default function DaySummary({ events }: { events: MarketEvent[] }) {
  const stats = useMemo(() => {
    const total = events.length;
    const high = events.filter((e) => e.impact === 'high').length;

    const freq = new Map<string, number>();
    const sess = new Map<string, number>();
    const buckets = new Map<number, number>();
    for (const e of events) {
      freq.set(e.currency, (freq.get(e.currency) ?? 0) + 1);
      sess.set(sessionOf(toUtcMinutes(e.time)), (sess.get(sessionOf(toUtcMinutes(e.time))) ?? 0) + 1);
      buckets.set(bucketOf(toUtcMinutes(e.time)), (buckets.get(bucketOf(toUtcMinutes(e.time))) ?? 0) + 1);
    }
    let mostActive = '';
    let m = 0;
    for (const [c, n] of freq) if (n > m) { m = n; mostActive = c; }

    let busiest = '';
    let b = 0;
    for (const [s, n] of sess) if (n > b) { b = n; busiest = s; }

    const peak = [...buckets.entries()].sort((a, z) => z[1] - a[1]).slice(0, 2).map(([bk]) => bucketLabel(bk));

    return { total, high, mostActive, busiest, peak: peak.join(' · ') };
  }, [events]);

  return (
    <section className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
        Tóm tắt ngày thị trường
      </h2>
      <p className="mt-1.5 text-sm text-slate-500">Các chỉ số khách quan về mật độ sự kiện trong ngày đã chọn.</p>

      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard label="Tổng sự kiện" value={String(stats.total)} icon="ri-calendar-event-line" />
        <StatCard label="Tác động cao" value={String(stats.high)} icon="ri-flashlight-line" tone="warn" />
        <StatCard label="Tiền tệ sôi động nhất" value={stats.mostActive || '—'} icon="ri-coins-line" tone="accent" />
        <StatCard label="Phiên bận nhất" value={stats.busiest || '—'} icon="ri-building-2-line" tone="accent" />
        <StatCard label="Mật độ sự kiện" value={stats.peak || '—'} icon="ri-bar-chart-2-line" />
      </div>
    </section>
  );
}